// TS 4.9 的 lib.dom.d.ts 有 FileSystem*Handle,但缺 createWritable / FileSystemWritableFileStream /
// Window.showDirectoryPicker,故在此全局补齐(ambient 增强,manifest.ts 等同 program 内文件也能吃到)。
declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>;
  }
  interface FileSystemWritableFileStream {
    write(data: string | BufferSource | Blob): Promise<void>;
    close(): Promise<void>;
    abort(reason?: unknown): Promise<void>;
  }
  interface FileSystemFileHandle {
    createWritable(options?: { keepExistingData?: boolean }): Promise<FileSystemWritableFileStream>;
  }
}

export function isFsAccessSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

export async function pickDownloadDirectory(): Promise<FileSystemDirectoryHandle> {
  if (!window.showDirectoryPicker) throw new Error('showDirectoryPicker unavailable');
  return window.showDirectoryPicker({ mode: 'readwrite' });
}

/** 沿 localPath 的目录段逐级 getDirectoryHandle,返回最后一段的父目录与文件名。 */
export async function getParentDirAndName(
  root: FileSystemDirectoryHandle,
  localPath: string,
  create: boolean,
): Promise<{ dir: FileSystemDirectoryHandle; fileName: string }> {
  const parts = localPath.split('/').filter(Boolean);
  const fileName = parts.pop() as string;
  let dir = root;
  for (const seg of parts) {
    dir = await dir.getDirectoryHandle(seg, { create });
  }
  return { dir, fileName };
}

export async function fileExists(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await dir.getFileHandle(name, { create: false });
    return true;
  } catch {
    return false;
  }
}

/** 流式把 body 写进 dir/name,返回字节数;出错则 abort + 尽力 removeEntry 清理残留。 */
export async function writeStream(
  dir: FileSystemDirectoryHandle,
  name: string,
  body: ReadableStream<Uint8Array>,
  onBytes?: (n: number) => void,
): Promise<number> {
  const fh = await dir.getFileHandle(name, { create: true });
  const writable = await fh.createWritable();
  let total = 0;
  try {
    const reader = body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        await writable.write(value);
        total += value.byteLength;
        onBytes?.(total);
      }
    }
    await writable.close();
    return total;
  } catch (e) {
    try { await writable.abort(); } catch { /* ignore */ }
    try { await dir.removeEntry(name); } catch { /* best-effort */ }
    throw e;
  }
}
